import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { Moon, Sun, Menu, PanelLeft, FileDown, CalendarRange } from "lucide-react";
import GlobalSearch from "./GlobalSearch";
import NotificationsMenu from "./NotificationsMenu";
import ExportDialog from "./ExportDialog";
import WeeklyReportDialog from "./WeeklyReportDialog";
import UserMenu from "./UserMenu";

export default function Topbar({ onMenuClick, onToggleCollapse }) {
  const { projects } = useData();
  const { user } = useAuth();
  const [dark, setDark] = useState(() => (localStorage.getItem("bwn-theme") || "light") === "dark");
  const [exportOpen, setExportOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("bwn-theme", dark ? "dark" : "light");
  }, [dark]);

  const crumbs = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "project") {
      const p = projects.find((x) => x.id === parts[1]);
      return p ? p.name : "Project";
    }
    if (parts[0] === "space") return "Space";
    if (parts[0] === "calendar") return "Calendar";
    if (parts[0] === "tasks") return "All Tasks";
    if (parts[0] === "whiteboards") return "Whiteboards";
    if (parts[0] === "files") return "Files";
    if (parts[0] === "omzet") return "Omzet & Revenue";
    if (parts[0] === "chat") return "Chat";
    if (parts[0] === "team") return "Team";
    return "Dashboard";
  }, [location.pathname, projects]);

  return (
    <header className="h-14 border-b border-border flex items-center gap-3 px-3 md:px-4 shrink-0 bg-background z-10" data-testid="topbar">
      <button data-testid="mobile-menu-button" onClick={onMenuClick}
        className="md:hidden h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0">
        <Menu size={17} />
      </button>
      <button data-testid="sidebar-collapse-toggle" onClick={onToggleCollapse}
        className="hidden md:flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0">
        <PanelLeft size={17} />
      </button>
      <h1 className="font-heading text-lg font-bold tracking-tight hidden sm:block" data-testid="topbar-title">{crumbs}</h1>
      <GlobalSearch />

      <div className="ml-auto flex items-center gap-1">
        {user?.role === "admin" && (
          <button data-testid="weekly-report-button" onClick={() => setWeeklyOpen(true)} title="Weekly report"
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <CalendarRange size={17} />
          </button>
        )}
        <button data-testid="export-report-button" onClick={() => setExportOpen(true)} title="Export report"
          className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <FileDown size={17} />
        </button>
        <NotificationsMenu />

        <button data-testid="theme-toggle" onClick={() => setDark((d) => !d)}
          className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          {dark ? <Sun size={17} /> : <Moon size={17} />}
        </button>

        <UserMenu />
      </div>
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      {weeklyOpen && <WeeklyReportDialog open={weeklyOpen} onClose={() => setWeeklyOpen(false)} />}
    </header>
  );
}
